import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { getPlacementGroupsForVariants } from 'domain/adPreview/placementGroups'
import {
  getFeedAdVariations,
  getReelsAdVariations,
} from 'domain/adPreview/metaSingleImageVariations'
import { getCarouselAdVariations } from 'domain/adPreview/carouselVariations'
import {
  getDisplay9x16Variations,
  getDisplay1x1Variations,
  getDisplay191x1Variations,
  getDisplayVideoVariations,
  getDisplayGeneralVariations,
} from 'domain/adPreview/googleDisplayVariations'
import {
  getDemandGen9x16Variations,
  getDemandGen1x1Variations,
  getDemandGen191x1Variations,
  getDemandGen4x5Variations,
  getDemandGenCarouselVariations,
} from 'domain/adPreview/googleDemandGenVariations'
import { isVariationDisabled } from 'components/Recommendations/Campaign/Context/disabledVariations'
import FeedAdCard from './AdCards/FeedAdCard'
import ReelsAdCard from './AdCards/ReelsAdCard'
import DisplayAdCard from './AdCards/GoogleDisplay'
import VariationToggleOverlay from './AdCards/VariationToggleOverlay'

function renderDisplayCard(ad, key) {
  return (
    <div key={key} className="preview-ads-google-card">
      <DisplayAdCard ad={ad} />
    </div>
  )
}

// Per-placement renderers, keyed by the placement-group `key`s from placementGroups.js.
// Demand Gen reuses Display's `DisplayAdCard`/`renderDisplayCard` — same skin
// components, different variations builder.
const PLACEMENT_RENDERERS = {
  'fb-feed-desktop': {
    getVariations: getFeedAdVariations,
    renderCard: (ad, key) => (
      <div key={key} className="preview-ads-meta-card preview-ads-meta-card--feed-desktop">
        <FeedAdCard platform="facebook" ad={ad} />
      </div>
    ),
  },
  'fb-feed-mobile': {
    getVariations: getFeedAdVariations,
    renderCard: (ad, key) => (
      <div key={key} className="preview-ads-meta-card preview-ads-meta-card--feed-mobile">
        <FeedAdCard platform="facebook" mobileView ad={ad} />
      </div>
    ),
  },
  'ig-feed': {
    getVariations: getFeedAdVariations,
    renderCard: (ad, key) => (
      <div key={key} className="preview-ads-meta-card preview-ads-meta-card--feed-mobile">
        <FeedAdCard platform="instagram" ad={ad} />
      </div>
    ),
  },
  'ig-reels': {
    getVariations: getReelsAdVariations,
    renderCard: (ad, key) => (
      <div key={key} className="preview-ads-meta-card preview-ads-meta-card--reels">
        <ReelsAdCard ad={ad} />
      </div>
    ),
  },
  'fb-feed-desktop-carousel': {
    getVariations: getCarouselAdVariations,
    renderCard: (ad, key) => (
      <div key={key} className="preview-ads-meta-card preview-ads-meta-card--feed-desktop-carousel">
        <FeedAdCard platform="facebook" ad={ad} />
      </div>
    ),
  },
  'fb-feed-mobile-carousel': {
    getVariations: getCarouselAdVariations,
    renderCard: (ad, key) => (
      <div key={key} className="preview-ads-meta-card preview-ads-meta-card--feed-mobile-carousel">
        <FeedAdCard platform="facebook" mobileView ad={ad} />
      </div>
    ),
  },
  'ig-feed-carousel': {
    getVariations: getCarouselAdVariations,
    renderCard: (ad, key) => (
      <div key={key} className="preview-ads-meta-card preview-ads-meta-card--feed-mobile">
        <FeedAdCard platform="instagram" ad={ad} />
      </div>
    ),
  },
  'ig-reels-carousel': {
    getVariations: getCarouselAdVariations,
    renderCard: (ad, key) => (
      <div key={key} className="preview-ads-meta-card preview-ads-meta-card--reels">
        <ReelsAdCard ad={ad} />
      </div>
    ),
  },
  'google-display-9x16': { getVariations: getDisplay9x16Variations, renderCard: renderDisplayCard },
  'google-display-1x1': { getVariations: getDisplay1x1Variations, renderCard: renderDisplayCard },
  'google-display-191x1': {
    getVariations: getDisplay191x1Variations,
    renderCard: renderDisplayCard,
  },
  'google-display-video': {
    getVariations: getDisplayVideoVariations,
    renderCard: renderDisplayCard,
  },
  'google-display-general': {
    getVariations: getDisplayGeneralVariations,
    renderCard: renderDisplayCard,
  },
  'google-demand-gen-9x16': {
    getVariations: getDemandGen9x16Variations,
    renderCard: renderDisplayCard,
  },
  'google-demand-gen-1x1': {
    getVariations: getDemandGen1x1Variations,
    renderCard: renderDisplayCard,
  },
  'google-demand-gen-191x1': {
    getVariations: getDemandGen191x1Variations,
    renderCard: renderDisplayCard,
  },
  'google-demand-gen-4x5': {
    getVariations: getDemandGen4x5Variations,
    renderCard: renderDisplayCard,
  },
  'google-demand-gen-carousel': {
    getVariations: getDemandGenCarouselVariations,
    renderCard: renderDisplayCard,
  },
}

// "Ad Placements" section scaffold: one section per placement group with a
// name + badge + scroll row. All placement types render real cards.
const PlacementGroups = ({
  creative,
  variants,
  platformFilter,
  selectedMediaId,
  disabledVariations = {},
  onToggleVariation,
}) => {
  const { t } = useTranslation()
  const groups = getPlacementGroupsForVariants(variants)
  const platformGroups =
    platformFilter === 'all' ? groups : groups.filter((group) => group.platform === platformFilter)

  // Resolve variations (and the asset-select filter) per group up front so
  // groups a filter leaves with zero cards can be dropped entirely, rather
  // than rendering an empty row under a "0 variations" header.
  const resolvedGroups = platformGroups
    .map((group) => {
      const renderer = PLACEMENT_RENDERERS[group.key]
      if (!renderer) return null
      const allVariations = renderer.getVariations(creative, t)
      // Carousel groups (`*-carousel` keys) have no singular `ad.media` to
      // filter on — a carousel card's assets live in `ad.carouselCards`
      // instead — so asset-select filtering only applies to non-carousel
      // groups, matching the sidebar's own per-image-vs-carousel split.
      const isCarouselGroup = group.key.endsWith('-carousel')
      const variations =
        selectedMediaId && !isCarouselGroup
          ? allVariations.filter((ad) => ad.media?.id === selectedMediaId)
          : allVariations

      return { group, renderer, variations }
    })
    .filter((resolved) => resolved && resolved.variations.length > 0)

  return (
    <div className="preview-ads-placement-groups">
      <h4 className="preview-ads-placement-groups__heading">
        {t('Recommendations.adPreview.adPlacements')}
      </h4>
      {resolvedGroups.length === 0 && (
        <p className="preview-ads-placement-groups__empty">
          {t('Recommendations.adPreview.noPlacements')}
        </p>
      )}
      {resolvedGroups.map(({ group, renderer, variations }) => (
        <div key={group.key} className="preview-ads-placement-group">
          <div className="preview-ads-placement-group__header">
            <span className="preview-ads-placement-group__name">{group.name}</span>
            <span className="preview-ads-placement-group__count">
              {t('Recommendations.adPreview.variations', { count: variations.length })}
            </span>
          </div>
          <div className="preview-ads-placement-group__row">
            {variations.map((ad, index) => {
              const key = `${group.key}-${index}`
              const card = renderer.renderCard(ad, key)

              // Only Meta cards carry a variationId (assigned by the
              // single-image and carousel builders), so only they can be
              // toggled here. Google creatives do have excluded_variations
              // — googleAdCount.js subtracts them — but a Google card is one
              // per placement skin, not one per variation, so there's no
              // card-to-variation mapping to toggle. onToggleVariation gates
              // only the *interactive* overlay: a read-only preview still has
              // to show an already-excluded variation as disabled.
              if (ad?.variationId == null) return card

              const variationDisabled = isVariationDisabled(
                disabledVariations,
                creative?.tc,
                ad.variationId,
              )

              return (
                <div
                  key={key}
                  className={`preview-ads-card-wrap ${variationDisabled ? 'preview-ads-card-wrap--disabled' : ''}`}
                >
                  {card}
                  {onToggleVariation && (
                    <VariationToggleOverlay
                      disabled={variationDisabled}
                      onToggle={() => onToggleVariation(ad)}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

PlacementGroups.propTypes = {
  creative: PropTypes.object.isRequired,
  variants: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  platformFilter: PropTypes.string.isRequired,
  selectedMediaId: PropTypes.string,
  disabledVariations: PropTypes.object,
  onToggleVariation: PropTypes.func,
}

export default PlacementGroups
